<template>
  <div :class="joinClasses('dropdown', modifiers)">
    <button
      :aria-label="$t('aria_label.filter.filter')"
      class="button button--transparent"
      type="button"
      @click="toggleFilter"
    >
      <span>{{ $t('general.filter') }}</span>
      <span
        v-if="filterCount"
        :aria-label="$t('aria_label.filter.count')"
        class="select__filter-count typo-tiny"
      >
        {{ filterCount }}
      </span>
      <div class="dropdown__filter-icon">
        <SlidersSvg />
      </div>
    </button>
    <Popper
      v-model:open="open"
      :modifiers="['cover', hasOrgFilter ? 'no-max-width' : null]"
      placement="bottom-end"
    >
      <div class="dropdown__content">
        <FilterMobileHeader
          :label="$t('general.filters')"
          @toggle="toggleFilter"
        />

        <div class="dropdown__body">
          <div
            :class="{
              dropdown__filter: true,
              'dropdown__filter--min-height-auto': hasOrgFilter
            }"
          >
            <div class="dropdown__section dropdown__section--border show-for-medium">
              <span class="header-text">{{ $t('general.filters') }}</span>
            </div>

            <!-- Sort by -->
            <div
              v-if="sortOptions.length > 0"
              class="dropdown__section dropdown__section--border"
            >
              <span class="sub-heading-text">{{ $t('general.sort_by') }}:</span>

              <div class="dropdown__input-group dropdown__group--sort">
                <div
                  v-for="sort in sortOptions"
                  :key="sort.value"
                  class="dropdown__input-field checkbox--rounded checkbox--bordered"
                >
                  <input
                    :id="sort.value"
                    v-model="sortOption"
                    class="checkbox checkbox__input"
                    name="sortOption"
                    type="radio"
                    :value="sort.value"
                  >
                  <label
                    class="checkbox__label typo-input"
                    :for="sort.value"
                  >
                    {{ sort.label }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Task type -->
            <div
              v-if="typeFilter"
              class="dropdown__section dropdown__section--border"
            >
              <span class="sub-heading-text">{{ $t('tasks.type') }}:</span>

              <div class="dropdown__input-group dropdown__group--types">
                <div
                  v-for="type in taskTypeOptions"
                  :key="type.value"
                  class="dropdown__input-field checkbox--bordered"
                >
                  <input
                    :id="`type_${type.value}`"
                    v-model="types"
                    class="checkbox__input"
                    :name="type.value"
                    type="checkbox"
                    :value="type.value"
                  >
                  <label
                    class="checkbox__label typo-input"
                    :for="`type_${type.value}`"
                  >
                    {{ type.label }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Condition rating -->
            <div
              v-if="conditionFilter"
              class="dropdown__section dropdown__section--border"
            >
              <span class="sub-heading-text">{{ $t('issues.condition_rating') }}:</span>

              <div class="dropdown__input-group dropdown__input-group-conditions">
                <div
                  v-for="rating in overallConditionOptions"
                  :key="rating.value"
                  class="checkbox--bordered"
                >
                  <input
                    :id="`condition_${rating.value}`"
                    v-model="conditions"
                    class="checkbox__input"
                    :name="rating.value"
                    type="checkbox"
                    :value="rating.value"
                  >
                  <label
                    class="checkbox__label typo-input"
                    :for="`condition_${rating.value}`"
                  >
                    {{ rating.label }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Repair priority -->
            <div
              v-if="priorityFilter"
              class="dropdown__section dropdown__section--border"
            >
              <span class="sub-heading-text">{{ $t('issues.repair_priority') }}:</span>

              <div class="dropdown__input-group dropdown__input-group-priorities">
                <div
                  v-for="rating in repairPriorityOptions"
                  :key="rating.value"
                  class="checkbox--bordered"
                >
                  <input
                    :id="`repair_${rating.value}`"
                    v-model="priorities"
                    class="checkbox__input"
                    :name="rating.value"
                    type="checkbox"
                    :value="rating.value"
                  >
                  <label
                    class="checkbox__label typo-input"
                    :for="`repair_${rating.value}`"
                  >
                    {{ rating.label }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Labels -->
            <div
              v-if="labelOptions.length > 0"
              class="dropdown__section dropdown__section--border"
            >
              <span class="sub-heading-text">{{ $t('tasks.labels') }}:</span>

              <div class="dropdown__input-group dropdown__group--labels">
                <div
                  v-for="label in labelOptions"
                  :key="label.value"
                  class="dropdown__input-field checkbox--bordered"
                >
                  <input
                    :id="`label_${label.value}`"
                    v-model="labels"
                    class="checkbox checkbox__input"
                    :name="label.value"
                    type="checkbox"
                    :value="label.value"
                  >
                  <label
                    class="checkbox__label typo-input"
                    :for="`label_${label.value}`"
                  >
                    {{ label.label }}
                  </label>
                </div>
              </div>
            </div>

            <div
              v-if="hasOrgFilter"
              class="dropdown__group dropdown__group--wrap dropdown__group--stack"
            >
              <!-- Mines section -->
              <div
                v-if="mineOptions.length > 0"
                class="dropdown__section dropdown__section--min-width"
              >
                <span class="sub-heading-text">{{ $t('equipment.mine') }}:</span>

                <div class="dropdown__input-group dropdown__group--mines">
                  <div
                    v-for="mine in mineOptions"
                    :key="mine.value"
                    class="dropdown__input-field checkbox--bordered"
                  >
                    <!-- Add Unclassified mine -->
                    <template v-if="mine.label">
                      <input
                        :id="`mine_${mine.value}`"
                        v-model="organisationMines"
                        class="checkbox__input"
                        :name="mine.value"
                        type="checkbox"
                        :value="mine.value"
                      >
                      <label
                        class="checkbox__label typo-input"
                        :for="`mine_${mine.value}`"
                      >
                        {{ mine.label }}
                      </label>
                    </template>
                    <SkeletonElement
                      v-else
                      :width="120"
                    />
                  </div>
                </div>
              </div>

              <!-- Processes section -->
              <div
                v-if="processOptions.length > 0"
                class="dropdown__section dropdown__section--min-width"
              >
                <span class="sub-heading-text">{{ $t('equipment.process') }}:</span>

                <div class="dropdown__input-group dropdown__group--processes">
                  <div
                    v-for="process in processOptions"
                    :key="process.value"
                    class="dropdown__input-field checkbox--bordered"
                  >
                    <template v-if="process.value && process.label">
                      <input
                        :id="`process_${process.value}`"
                        v-model="organisationProcesses"
                        class="checkbox__input"
                        :name="process.value"
                        type="checkbox"
                        :value="process.value"
                      >
                      <label
                        class="checkbox__label typo-input"
                        :for="`process_${process.value}`"
                      >
                        {{ process.label }}
                      </label>
                    </template>
                    <SkeletonElement
                      v-else
                      :width="120"
                    />
                  </div>
                </div>
              </div>

              <!-- Types section -->
              <div
                v-if="typeOptions.length > 0"
                class="dropdown__section dropdown__section--min-width"
              >
                <span class="sub-heading-text">{{ $t('equipment.type') }}:</span>

                <div class="dropdown__input-group dropdown__group--types">
                  <div
                    v-for="type in typeOptions"
                    :key="type.value"
                    class="dropdown__input-field checkbox--bordered"
                  >
                    <template v-if="type.value && type.label">
                      <input
                        :id="`organisation_type_${type.value}`"
                        v-model="organisationTypes"
                        class="checkbox__input"
                        :name="type.value"
                        type="checkbox"
                        :value="type.value"
                      >
                      <label
                        class="checkbox__label typo-input"
                        :for="`organisation_type_${type.value}`"
                      >
                        {{ type.label }}
                      </label>
                    </template>
                    <SkeletonElement
                      v-else
                      :width="120"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div
            :class="{
              'dropdown__group dropdown__group--actions': true,
              'dropdown__group--hide': !open
            }"
          >
            <button
              class="button button--danger"
              type="button"
              @click="clearAll"
            >
              <span>{{ $t('general.clear_all') }}</span>
            </button>

            <div class="dropdown__group">
              <button
                class="button show-for-medium"
                type="button"
                @click="toggleFilter"
              >
                <span>{{ $t('general.cancel') }}</span>
              </button>

              <button
                class="button button--primary button--auto"
                type="button"
                @click="apply"
              >
                <span>{{ $t('general.apply') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Popper>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

// Utils
import I18n from 'i18n-js';
import { DEFAULT_RATINGS } from '@/functions/simm/deterioriation_mechanisms';
import joinClasses from '@/functions/join_classes';
import useOrganisationProfile from '@/functions/use_organisation_profile';
import { PRIORITY_FILTER_VALUES } from '@/functions/priority';

// Components
import Popper from '@/components/generic/popper.vue';
import SkeletonElement from '@/components/generic/skeleton_element.vue';
import FilterMobileHeader from '@/components/generic/date_filter_mobile_header.vue';

// Svg
import SlidersSvg from '@/images/sliders.svg';

const props = defineProps({
  /** Additional classes you may add */
  modifiers: { type: Array, default: () => [] },
  /** Sort options */
  sortOptions: { type: Array, required: false, default: () => [] },
  /** Label options */
  labelOptions: { type: Array, required: false, default: () => [] },
  /** Show/hide the Type options */
  typeFilter: { type: Boolean, default: true },
  /** Show/hide the Priority options */
  priorityFilter: { type: Boolean, default: true },
  /** Show/hide the Condition options */
  conditionFilter: { type: Boolean, default: true },
  /** Mine options */
  mineOptions: { type: Array, required: false, default: () => [] },
  /** Process options */
  processOptions: { type: Array, required: false, default: () => [] },
  /** Type options */
  typeOptions: { type: Array, required: false, default: () => [] },
});

const emit = defineEmits(['setFilterOptions']);

const open = ref(false);
const sortOption = ref(null);
const conditions = ref([]);
const priorities = ref([]);
const types = ref([]);
const labels = ref([]);
const organisationMines = ref([]);
const organisationProcesses = ref([]);
const organisationTypes = ref([]);
const filterCount = ref(null);

const { priorityLabel } = useOrganisationProfile();

const overallConditionOptions = DEFAULT_RATINGS.map((rating) => ({
  label: rating.toString(),
  value: rating,
})).filter((rating) => rating.value !== 0);

const repairPriorityOptions = computed(() => PRIORITY_FILTER_VALUES.map((priority) => ({
  label: priorityLabel(priority),
  value: priority,
})));

/**
 * Filter (type) options
 */
const taskTypeOptions = [
  'protection_housekeeping',
  'immediate_securing_action',
  'repair_replace',
  'scoping_job',
].map((type) => ({
  value: type,
  label: I18n.t(`tasks.type_names.${type}`),
}));

const hasOrgFilter = computed(() => {
  return props.mineOptions.length > 0 || props.processOptions.length > 0 || props.typeOptions.length > 0;
});

function setFilterCount() {
  filterCount.value = (
    (sortOption.value ? 1 : 0) +
    conditions.value.length +
    priorities.value.length +
    types.value.length +
    labels.value.length +
    organisationMines.value.length +
    organisationProcesses.value.length +
    organisationTypes.value.length
  );
}

function toggleFilter() {
  open.value = !open.value;
}

function prepareFilterOptions() {
  return {
    sortOption: sortOption.value,
    conditions: Object.values(conditions.value),
    priorities: Object.values(priorities.value),
    types: Object.values(types.value),
    labels: Object.values(labels.value),
    organisationMines: Object.values(organisationMines.value),
    organisationProcesses: Object.values(organisationProcesses.value),
    organisationTypes: Object.values(organisationTypes.value),
  };
}

function clearAll() {
  sortOption.value = null;
  conditions.value = [];
  priorities.value = [];
  types.value = [];
  labels.value = [];
  organisationMines.value = [];
  organisationProcesses.value = [];
  organisationTypes.value = [];
  emit('setFilterOptions', prepareFilterOptions());
  setFilterCount();
}

function apply() {
  emit('setFilterOptions', prepareFilterOptions());
  setFilterCount();
  toggleFilter();
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

@mixin content-size {
  min-width: bu(41.5);
  height: auto;
}

.checkbox__label {
  &:before {
    height: bu(2.5);
    width: bu(2.5);
  }
}

.dropdown {
  display: block;

  &--no-padding-right > .button {
    padding-right: 0;
  }

  &--normal-font-weight {
    .button--transparent {
      font-weight: normal;
    }
  }

  &--grey {
    .button {
      color: map-get($foreground-color, dark);
    }
  }

  &__section {
    padding: bu(2) 0;
    margin: 0 bu(2.5);

    &--border {
      border-bottom: bu(0.1) solid map-get($foreground-color, darker);
    }

    &--min-width {
      min-width: bu(18);
      max-width: bu(20);
    }

    span.header-text {
      font-weight: 500;
      color: map-get($foreground-color, light);
    }
  }

  &__input-field {
    padding-top: bu(1);
  }

  &__filter-icon {
    padding-left: bu(0.5);
  }

  &__label {
    @include breakpoint(small) {
      padding: bu(2.5) bu(1.4);
    }

    @include breakpoint(medium) {
      padding: bu(2.5) 0;
    }
  }

  &__content {
    z-index: 1;

    @include breakpoint(small) {
      height: calc(100vh - bu(15));
      width: 100%;
    }

    @include breakpoint(medium) {
      @include content-size();
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: map-get($background-color, light);
    z-index: 1;
    box-shadow: map-get($box-shadow, popover);
    position: relative;
    overflow-x: hidden;
    padding-bottom: bu(3);

    @include breakpoint(small) {
      width: 100%;
      height: 100%;
      border-radius: 0;
      justify-content: unset;
    }

    @include breakpoint(medium) {
      @include content-size();
      border-radius: bu(1.4);
      padding-bottom: bu(1);
    }
  }

  &__filter {
    overflow: auto;
    min-height: unset;
    max-height: unset;

    @include breakpoint(medium) {
      min-height: 400px;
      max-height: calc(100vh - 400px);
      padding-bottom: bu(1);

      &--min-height-auto {
        min-height: auto;
      }
    }
  }

  &__input-group {
    display: flex;
    justify-content: space-between;
    color: map-get($foreground-color, light);
    padding: bu(1.375) 0 0;
  }

  &__group {
    display: flex;
    justify-content: space-between;

    @include breakpoint(small) {
      padding: 0 bu(1.4);
    }

    @include breakpoint(medium) {
      padding: 0;
    }

    &--wrap {
      flex-wrap: wrap;
    }

    &--stack {
      @include breakpoint(small) {
        flex-direction: column;
      }

      @include breakpoint(medium) {
        flex-direction: row;
      }
    }

    &--sort,
    &--types,
    &--labels,
    &--mines,
    &--processes {
      padding-top: 0;
      flex-direction: column;
    }

    &--actions {
      border-top: bu(0.1) solid map-get($foreground-color, darker);
      .button {
        color: $white;
      }

      .button--primary {
        color: map-get($foreground-color, darkest);
      }

      @include breakpoint(small) {
        background: map-get($background-color, light);
        position: fixed;
        width: 100%;
        padding: bu(2) bu(7) bu(3) bu(2.5);
        bottom: 0;
      }

      @include breakpoint(medium) {
        position: relative;
        padding: bu(2) bu(2.5);
      }
    }

    &--hide {
      display: none;
    }
  }
}
</style>
